import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { openPanelInCanvas, getEditor, ejectGlobalPanelsFromSiteFrames } from 'agentable-canvas/whiteboard';
import { openOnboardingChatPanel, openProjectBriefPanel } from '../../hooks/useOnboardingPanelsBridge';
import { dispatchOpenSiteOnCanvas } from '../../hooks/useSitePanelsBridge';
import {
  Building2,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Globe,
  Layers,
  Link2,
  MessageSquare,
  PanelLeftClose,
  PanelLeftOpen,
  Pencil,
  Plus,
  Rocket,
  Search,
  Settings,
  Shield,
  Sun,
  Upload,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getUserDisplayName, getUserInitials } from '../../lib/auth-utils';
import { useUiOverlay } from '../../contexts/useUiOverlay';
import { useCanvasSession } from '../../contexts/CanvasSessionContext';
import { getRecentSites, siteTitleFromSession, type RecentSite } from '../../lib/recent-sites';
import { ContextLayersList } from './ContextLayersList';

/** Quick-nav actions dispatched for the canvas layer to consume. */
export type CanvasQuickNavAction =
  | 'open-in-canvas'
  | 'preview-edit'
  | 'settings'
  | 'chat'
  | 'seo'
  | 'collections'
  | 'publish';

function dispatchQuickNav(action: CanvasQuickNavAction, siteId: string | null): void {
  window.dispatchEvent(
    new CustomEvent('landi:canvas-quicknav', {
      detail: { action, siteId, timestamp: new Date().toISOString() },
      bubbles: true,
      composed: true,
    }),
  );
}

function SidebarFooter(): ReactElement {
  const { user, isLoading, isConfigured, openLogin, signOut } = useAuth();

  if (!isConfigured) {
    return (
      <footer className="vibe-sidebar__footer">
        <p className="vibe-sidebar__auth-hint">Configure Supabase env for sign-in.</p>
      </footer>
    );
  }

  if (isLoading) {
    return (
      <footer className="vibe-sidebar__footer">
        <p className="vibe-sidebar__auth-hint">Loading account…</p>
      </footer>
    );
  }

  if (!user) {
    return (
      <footer className="vibe-sidebar__footer">
        <button type="button" className="vibe-sidebar__sign-in-btn" onClick={() => openLogin('to save your work')}>
          Sign in
        </button>
      </footer>
    );
  }

  const initials = getUserInitials(user);
  const displayName = getUserDisplayName(user);

  return (
    <footer className="vibe-sidebar__footer">
      <div className="vibe-sidebar__footer-tools">
        <button type="button" className="vibe-sidebar__icon-btn" aria-label="Toggle theme">
          <Sun className="vibe-sidebar__icon" aria-hidden />
        </button>
        <button type="button" className="vibe-sidebar__icon-btn" aria-label="Settings">
          <Settings className="vibe-sidebar__icon" aria-hidden />
        </button>
      </div>
      <button type="button" className="vibe-sidebar__user-row" aria-label="Account menu" onClick={() => void signOut()}>
        <span className="vibe-sidebar__avatar" aria-hidden>
          {initials}
        </span>
        <span className="vibe-sidebar__user-name">{displayName}</span>
        <span className="vibe-sidebar__upgrade-pill">Upgrade</span>
        <ChevronDown className="vibe-sidebar__icon-sm vibe-sidebar__chevron" aria-hidden />
      </button>
    </footer>
  );
}

interface AccordionGroupProps {
  id: string;
  label: string;
  icon: ReactElement;
  expanded: boolean;
  onToggle: (id: string) => void;
  /** Rendered between the group header and expandable sub-items (always visible). */
  betweenHeaderAndItems?: ReactElement | null;
  children: ReactElement | ReactElement[];
}

/** Collapsible nav group (accordion header + sub-items region). */
function AccordionGroup({
  id,
  label,
  icon,
  expanded,
  onToggle,
  betweenHeaderAndItems,
  children,
}: AccordionGroupProps): ReactElement {
  const regionId = `vibe-sidebar-group-${id}`;
  const expandedProps = { 'aria-expanded': expanded, 'aria-controls': regionId };
  return (
    <div className="vibe-sidebar__group" data-expanded={expanded}>
      <button
        type="button"
        className="vibe-sidebar__group-header"
        {...expandedProps}
        onClick={() => onToggle(id)}
      >
        <span className="vibe-sidebar__group-header-main">
          {icon}
          <span>{label}</span>
        </span>
        <ChevronRight
          className={`vibe-sidebar__icon-sm vibe-sidebar__group-caret${expanded ? ' vibe-sidebar__group-caret--open' : ''}`}
          aria-hidden
        />
      </button>
      {betweenHeaderAndItems}
      {expanded ? (
        <div className="vibe-sidebar__group-items" id={regionId} role="group" aria-label={label}>
          {children}
        </div>
      ) : null}
    </div>
  );
}

function SubItem({
  label,
  icon,
  active,
  onClick,
}: {
  label: string;
  icon?: ReactElement;
  active?: boolean;
  onClick: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      className={`vibe-sidebar__subitem${active ? ' vibe-sidebar__subitem--active' : ''}`}
      onClick={onClick}
    >
      {icon ? <span className="vibe-sidebar__subitem-icon" aria-hidden>{icon}</span> : null}
      <span className="vibe-sidebar__subitem-label">{label}</span>
    </button>
  );
}

/**
 * vibe.landi.build-style sidebar: collapsible accordion nav
 * (Websites / Agency / Superadmin) plus a Context quick-nav bound to the active
 * site. Only the group relevant to the active page is expanded by default.
 */
export function CollapsibleSidebarOverlay(): ReactElement {
  const { sidebarOpen, toggleSidebar, openSidebar, showHome, hideHome, homeVisible } = useUiOverlay();
  const { session, publishSite, loadSite } = useCanvasSession();

  const activeSiteId = session.siteId;
  const hasActiveSite = Boolean(activeSiteId);
  const activeSiteTitle = useMemo(() => siteTitleFromSession(session), [session]);
  const onCanvas = !homeVisible;

  const [recentSites, setRecentSites] = useState<RecentSite[]>([]);

  // Refresh recent sites whenever the sidebar opens or the active site changes,
  // so newly generated/updated sites appear without a reload.
  useEffect(() => {
    if (sidebarOpen) {
      setRecentSites(getRecentSites(3));
    }
  }, [sidebarOpen, activeSiteId, session.updatedAt]);

  // Accordion: default the contextually-relevant group open.
  const defaultGroup = hasActiveSite && onCanvas ? 'context' : 'websites';
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set([defaultGroup]));

  const toggleGroup = useCallback((id: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const goToNewChat = useCallback(() => {
    showHome();
    openSidebar();
  }, [showHome, openSidebar]);

  const openRecentSite = useCallback(
    (siteId: string) => {
      const loaded = loadSite(siteId);
      if (!loaded) {
        return;
      }
      hideHome();
      dispatchOpenSiteOnCanvas({ siteId });
    },
    [loadSite, hideHome],
  );

  const openAllSites = useCallback(() => {
    hideHome();
    const editor = getEditor();
    if (editor) {
      ejectGlobalPanelsFromSiteFrames(editor);
    }
    openPanelInCanvas('all-sites', {
      focus: true,
      assignToSiteGroup: false,
      size: { w: 520, h: 640 },
      panelProps: { __title: 'All Sites' },
    });
  }, [hideHome]);

  const runQuickNav = useCallback(
    (action: CanvasQuickNavAction) => {
      if (action === 'publish') {
        void publishSite();
      } else {
        // Reveal the canvas workspace (chat + preview/edit + details panels)
        // then let the canvas layer respond to the specific target.
        hideHome();
      }
      dispatchQuickNav(action, activeSiteId);
      if (action === 'chat') {
        openOnboardingChatPanel();
      }
      if (action === 'settings') {
        hideHome();
        openProjectBriefPanel();
      }
    },
    [publishSite, hideHome, activeSiteId],
  );

  return (
    <>
      {!sidebarOpen ? (
        <button
          type="button"
          className="vibe-sidebar-toggle"
          data-testid="sidebar-toggle-collapsed"
          aria-label="Open sidebar"
          onClick={toggleSidebar}
        >
          <PanelLeftOpen className="vibe-sidebar__icon" aria-hidden />
        </button>
      ) : null}

      {sidebarOpen ? (
        <aside
          className="vibe-sidebar vibe-sidebar--open"
          data-testid="collapsible-sidebar-overlay"
          aria-label="Navigation sidebar"
        >
          <header className="vibe-sidebar__header">
            <button type="button" className="vibe-sidebar__logo-btn" onClick={showHome} aria-label="LANDIBUILD home">
              <img src="/logo-red.png" alt="LANDI" className="vibe-sidebar__logo-img" />
            </button>
            <button
              type="button"
              className="vibe-sidebar__icon-btn vibe-sidebar__collapse-btn"
              aria-label="Collapse sidebar"
              onClick={toggleSidebar}
            >
              <PanelLeftClose className="vibe-sidebar__icon" aria-hidden />
            </button>
          </header>

          <div className="vibe-sidebar__new-chat-wrap">
            <button type="button" className="vibe-sidebar__new-chat" onClick={goToNewChat}>
              <span className="vibe-sidebar__new-chat-left">
                <Plus className="vibe-sidebar__icon" aria-hidden />
                <span>New Chat</span>
              </span>
              <kbd className="vibe-sidebar__kbd">Ctrl K</kbd>
            </button>
          </div>

          <nav className="vibe-sidebar__nav vibe-sidebar__nav--accordion" aria-label="Primary">
            <AccordionGroup
              id="websites"
              label="Websites"
              icon={<Globe className="vibe-sidebar__icon" aria-hidden />}
              expanded={expandedGroups.has('websites')}
              onToggle={toggleGroup}
            >
              <SubItem label="All Sites" onClick={openAllSites} />
              {recentSites.length > 0 ? (
                recentSites.map((site) => (
                  <SubItem
                    key={site.siteId}
                    label={site.title}
                    active={site.siteId === activeSiteId && onCanvas}
                    onClick={() => openRecentSite(site.siteId)}
                  />
                ))
              ) : (
                <p className="vibe-sidebar__empty">No sites yet — start a new chat.</p>
              )}
            </AccordionGroup>

            <AccordionGroup
              id="agency"
              label="Agency"
              icon={<Building2 className="vibe-sidebar__icon" aria-hidden />}
              expanded={expandedGroups.has('agency')}
              onToggle={toggleGroup}
            >
              <SubItem
                label="Domains"
                icon={<Link2 className="vibe-sidebar__icon-sm" aria-hidden />}
                onClick={() => dispatchQuickNav('settings', activeSiteId)}
              />
              <SubItem
                label="Settings"
                icon={<Settings className="vibe-sidebar__icon-sm" aria-hidden />}
                onClick={() => dispatchQuickNav('settings', activeSiteId)}
              />
            </AccordionGroup>

            <div className="vibe-sidebar__group">
              <button
                type="button"
                className="vibe-sidebar__group-header"
                onClick={showHome}
              >
                <span className="vibe-sidebar__group-header-main">
                  <Shield className="vibe-sidebar__icon" aria-hidden />
                  <span>Superadmin</span>
                </span>
              </button>
            </div>
          </nav>

          <div className="vibe-sidebar__divider" role="separator" />

          <section className="vibe-sidebar__context" aria-label="Active context">
            <h2 className="vibe-sidebar__context-title">Context</h2>
            {hasActiveSite ? (
              <AccordionGroup
                id="context"
                label={activeSiteTitle}
                icon={<Layers className="vibe-sidebar__icon" aria-hidden />}
                expanded={expandedGroups.has('context')}
                onToggle={toggleGroup}
                betweenHeaderAndItems={
                  onCanvas && activeSiteId ? (
                    <ContextLayersList siteId={activeSiteId} enabled />
                  ) : null
                }
              >
                <SubItem
                  label="Open in Canvas"
                  icon={<Layers className="vibe-sidebar__icon-sm" aria-hidden />}
                  onClick={() => runQuickNav('open-in-canvas')}
                />
                <SubItem
                  label="Preview/Edit"
                  icon={<Pencil className="vibe-sidebar__icon-sm" aria-hidden />}
                  onClick={() => runQuickNav('preview-edit')}
                />
                <SubItem
                  label="Settings"
                  icon={<Settings className="vibe-sidebar__icon-sm" aria-hidden />}
                  onClick={() => runQuickNav('settings')}
                />
                <SubItem
                  label="Chat"
                  icon={<MessageSquare className="vibe-sidebar__icon-sm" aria-hidden />}
                  onClick={() => runQuickNav('chat')}
                />
                <SubItem
                  label="SEO"
                  icon={<Search className="vibe-sidebar__icon-sm" aria-hidden />}
                  onClick={() => runQuickNav('seo')}
                />
                <SubItem
                  label="Collections"
                  icon={<FolderOpen className="vibe-sidebar__icon-sm" aria-hidden />}
                  onClick={() => runQuickNav('collections')}
                />
                <SubItem
                  label="Publish"
                  icon={<Upload className="vibe-sidebar__icon-sm" aria-hidden />}
                  onClick={() => runQuickNav('publish')}
                />
              </AccordionGroup>
            ) : (
              <p className="vibe-sidebar__empty">
                <Rocket className="vibe-sidebar__icon-sm" aria-hidden /> No active site. Generate or open a site to see its
                tools here.
              </p>
            )}
          </section>

          <SidebarFooter />
        </aside>
      ) : null}
    </>
  );
}
